import { useImobiliaria } from './useImobiliaria';
import { useSiteConfig } from './useSiteConfig';
import type { SiteConfig } from '@/integrations/supabase/types';

/**
 * Hook centralizado para gerenciar todas as configurações do site
 * Combina dados de imobiliaria e site_imobiliaria com fallbacks inteligentes
 */
export function useSiteConfiguration(subdominio: string) {
  const { data: imobiliaria, isLoading: loadingImobiliaria, error: errorImobiliaria } = useImobiliaria(subdominio);
  const { data: siteConfig, isLoading: loadingSiteConfig, error: errorSiteConfig } = useSiteConfig(imobiliaria?.id);

  // Extrair configurações do JSON
  const config = siteConfig?.configuracoes as SiteConfig | null;

  // Configuração consolidada com fallbacks
  const configuration = {
    // Dados da Imobiliária
    imobiliaria: {
      id: imobiliaria?.id,
      nome: imobiliaria?.nome,
      cnpj: imobiliaria?.cnpj,
      telefone: imobiliaria?.telefone,
      subdominio: imobiliaria?.subdominio,
      urlSite: imobiliaria?.url_site,
    },

    // Branding
    branding: {
      nomeSite: config?.nome_site || imobiliaria?.nome || 'Imobiliária',
      logoUrl: siteConfig?.logo_url,
      capaUrl: siteConfig?.capa_url,
      bannerUrl: siteConfig?.banner_url,
      slogan: config?.slogan,
      descricaoCurta: config?.descricao_curta,
    },

    // Contato
    contato: {
      email: siteConfig?.email_contato || imobiliaria?.email_login,
      telefone: siteConfig?.telefone_contato || imobiliaria?.telefone,
      whatsapp: config?.whatsapp || siteConfig?.telefone_contato || imobiliaria?.telefone,
      whatsappMensagemPadrao: config?.whatsapp_mensagem_padrao || 'Olá! Gostaria de mais informações.',
      instagram: siteConfig?.instagram_url,
      creci: siteConfig?.creci,
    },

    // Conteúdo das Páginas
    conteudo: {
      // Home
      tituloPrincipal: siteConfig?.titulo_principal || `Encontre seu imóvel ideal`,
      textoPrincipal: siteConfig?.texto_principal || `Imóveis selecionados com qualidade e transparência`,
      
      // Destaques
      tituloDestaques: config?.titulo_destaques || 'Imóveis em Destaque',
      subtituloDestaques: config?.subtitulo_destaques,
      
      // Sobre
      tituloSobre: config?.titulo_sobre || `A ${imobiliaria?.nome || 'Imobiliária'}`,
      descricaoSobre: config?.descricao_sobre || `Somos uma imobiliária comprometida em conectar pessoas aos melhores imóveis.`,
      
      // Anuncie
      tituloAnuncie: config?.titulo_anuncie || `Anuncie na ${imobiliaria?.nome || 'Imobiliária'}`,
      subtituloAnuncie: config?.subtitulo_anuncie || 'Seu imóvel em destaque para quem está buscando de verdade.',
      
      // Secundário
      tituloSecundario: siteConfig?.titulo_secundario,
      textoSecundario: siteConfig?.texto_secundario,
      
      // Terciário
      tituloTerciario: siteConfig?.titulo_terciario,
      textoTerciario: siteConfig?.texto_terciario,
    },

    // Recursos
    recursos: {
      carrosselAtivo: siteConfig?.carrossel_ativo ?? true,
      mostrarMapa: config?.mostrar_mapa ?? true,
      mostrarTourVirtual: config?.mostrar_tour_virtual ?? true,
      mostrarValores: config?.mostrar_valores ?? true,
    },

    // SEO
    seo: {
      title: config?.meta_title || `${imobiliaria?.nome} - Imóveis`,
      description: config?.meta_description || `Encontre os melhores imóveis com ${imobiliaria?.nome}`,
      keywords: config?.meta_keywords || [],
    },

    // Analytics
    analytics: {
      googleAnalyticsId: config?.google_analytics_id,
      facebookPixelId: config?.facebook_pixel_id,
    },

    // Tema (futuro)
    tema: {
      corPrimaria: config?.cor_primaria,
      corSecundaria: config?.cor_secundaria,
      corDestaque: config?.cor_destaque,
    },

    // Status
    status: {
      siteAtivo: siteConfig?.ativo ?? false,
      sitePublicado: siteConfig?.status === 'publicado',
      imobiliariaAtiva: imobiliaria?.status === 'ativo',
    },

    // Métricas
    metricas: {
      totalVisitas: siteConfig?.total_visitas || 0,
      ultimaVisita: siteConfig?.ultima_visita,
    },
  };

  return {
    configuration,
    isLoading: loadingImobiliaria || loadingSiteConfig,
    error: errorImobiliaria || errorSiteConfig,
    imobiliaria,
    siteConfig,
    refetch: () => {
      // Implementar refetch se necessário
    },
  };
}

// Hook auxiliar para acessar configurações específicas
export function useBranding(subdominio: string) {
  const { configuration, isLoading } = useSiteConfiguration(subdominio);
  return { ...configuration.branding, isLoading };
}

export function useContato(subdominio: string) {
  const { configuration, isLoading } = useSiteConfiguration(subdominio);
  return { ...configuration.contato, isLoading };
}

export function useConteudo(subdominio: string) {
  const { configuration, isLoading } = useSiteConfiguration(subdominio);
  return { ...configuration.conteudo, isLoading };
}

// Função helper para abrir WhatsApp
export function openWhatsApp(numero: string, mensagem?: string) {
  const msg = mensagem || 'Olá! Gostaria de mais informações.';
  const whatsappUrl = `https://wa.me/${numero.replace(/\D/g, '')}?text=${encodeURIComponent(msg)}`;
  window.open(whatsappUrl, '_blank');
}
